import React, { useEffect, useState } from "react";
import { Col, Empty, Row, Select, Space, Table, Typography } from "antd";
import rewardsColumns from "./table-config";
import FundsService from "@services/Funds.service";
import ReactApexChart, { ApexOptions } from "react-apexcharts";
import moment from "moment";
import {
  numberFormatter,
  numberWithDelimiter,
} from "@features/utils/functions";
import Big from "big.js";

import * as styles from "./RewardsTableStyles.module.scss";

export type StakingReward = {
  amount: number;
  amount_usd: number;
  balance: number;
  balance_usd: number;
  created_at: string;
  transaction_id: number;
  type: string;
};

enum TimePeriods {
  last_month = "last_month",
  last_six_months = "last_six_months",
  ytd = "ytd",
  all = "all",
}

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  currencyDisplay: "code",
});

const RewardsTable = ({ investorId }: { investorId: number }) => {
  const [rewards, setRewards] = useState<StakingReward[]>([]);

  useEffect(() => {
    FundsService.getRewards(investorId).then(
      (resp: { data: StakingReward[] }) => {
        setRewards([...resp.data]);
      }
    );
  }, []);

  const state = {
    series: [
      {
        data: rewards?.map((reward, index) => {
          const cumulativeAmountUsd = rewards
            .slice(0, index + 1)
            .reduce((acc, cur) => acc + cur.balance_usd, 0);

          const cumulativeAmount = rewards
            .slice(0, index + 1)
            .reduce((acc, cur) => acc + cur.amount, 0);

          return [reward.created_at, cumulativeAmount, cumulativeAmountUsd];
        }),
      },
    ],
    options: {
      chart: {
        id: "area-datetime",
        type: "area",
        height: 350,
        toolbar: {
          autoSelected: "pan",
          show: false,
        },
      },
      colors: ["#BE39F6"],
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 0,
        style: "hollow",
      },
      crosshairs: {
        fill: {
          type: "solid",
          color: "#B1B9C4",
        },
      },
      xaxis: {
        type: "datetime",
        x: new Date(rewards[0]?.created_at || 0).getTime(),
        labels: {
          show: true,
        },
        tooltip: {
          enabled: false,
        },
      },
      yaxis: {
        labels: {
          show: true,
          formatter: (value: number) => {
            return numberFormatter(value);
          },
        },
      },
      tooltip: {
        custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
          var data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];

          const ethAmountUsd =
            numberWithDelimiter(Big(data[2]).round(2).toString(), " ") + " USD";
          const ethAmount =
            numberWithDelimiter(Big(data[1]).round(8).toString(), " ") + " ETH";

          const date = moment(data[0]).format("DD MMMM YYYY");

          return `<div>
            <div class="apexcharts-tooltip-series-group" style="display: flex; flex-direction: column; align-items: self-start;"> 
            <span class="apexcharts-tooltip-text-y-value">${ethAmount}</span>
            <span class="apexcharts-tooltip-text-y-value-in-usd">${ethAmountUsd}</span>
            
            <div class="apexcharts-tooltip-title">
              <span class="apexcharts-tooltip-text-y-value-in-usd">${date}</span>
            </div>

            </div
            </div>`;
        },
        marker: {
          show: false,
        },
      },
      grid: {
        padding: {
          left: 25,
          right: 25,
        },
      },
      fill: {
        type: "gradient",
        gradient: {
          colorStops: [
            [
              {
                offset: 0,
                color: "#5f2ead19",
                opacity: 1,
              },
              {
                offset: 30,
                color: "#f1def920",
                opacity: 1,
              },
              {
                offset: 100,
                color: "white",
                opacity: 1,
              },
            ],
          ],
        },
      },
    },
    selection: "one_year",
  };

  const handleTimePeriodChange = (selected: string) => {
    switch (selected) {
      case TimePeriods.last_month: {
        const newestData = moment.utc(rewards[0].created_at);
        const oldestData = moment.utc(rewards[rewards.length - 1].created_at);

        const dataFromLastMonth = newestData.clone().subtract(1, "month");
        const isSameOrAfter = dataFromLastMonth.isSameOrAfter(oldestData);
        const startDate = isSameOrAfter ? dataFromLastMonth : oldestData;

        ApexCharts.exec(
          "area-datetime",
          "zoomX",
          startDate.unix() * 1000,
          newestData.unix() * 1000
        );
        return;
      }

      case TimePeriods.last_six_months: {
        const newestData = moment.utc(rewards[0].created_at);
        const oldestData = moment.utc(rewards[rewards.length - 1].created_at);

        const dataFromLastMonth = newestData.clone().subtract(6, "months");
        const isSameOrAfter = dataFromLastMonth.isSameOrAfter(oldestData);
        const startDate = isSameOrAfter ? dataFromLastMonth : oldestData;

        ApexCharts.exec(
          "area-datetime",
          "zoomX",
          startDate.unix() * 1000,
          newestData.unix() * 1000
        );
        return;
      }

      case TimePeriods.ytd: {
        const newestData = moment.utc(rewards[0].created_at);
        const oldestData = moment.utc(rewards[rewards.length - 1].created_at);

        const currentYear = moment().startOf("year");
        const isSameOrAfter = currentYear.isSameOrAfter(oldestData);
        const startDate = isSameOrAfter ? currentYear : oldestData;

        ApexCharts.exec(
          "area-datetime",
          "zoomX",
          startDate.unix() * 1000,
          newestData.unix() * 1000
        );
        return;
      }

      case TimePeriods.all:
      default: {
        const newestData = moment.utc(rewards[0].created_at);
        const oldestData = moment.utc(rewards[rewards.length - 1].created_at);

        ApexCharts.exec(
          "area-datetime",
          "zoomX",
          oldestData.unix() * 1000,
          newestData.unix() * 1000
        );
        return;
      }
    }
  };

  if (rewards.length === 0) return null;

  return (
    <Row>
      <Col xs={{ span: 24 }} lg={{ span: 24 }}>
        <div className={styles.chartWrapper}>
          <Space style={{ justifyContent: "space-between" }}>
            <Typography.Title level={3} style={{ fontWeight: 700 }}>
              Rewards
            </Typography.Title>

            {rewards.length > 1 && (
              <Select
                style={{ width: "100%", fontSize: "12px", marginRight: "30px" }}
                onChange={handleTimePeriodChange}
                defaultValue={TimePeriods.all}
                getPopupContainer={(triggerNode) =>
                  triggerNode.parentElement ?? triggerNode
                }
                options={[
                  { value: TimePeriods.last_month, label: "Last Month" },
                  {
                    value: TimePeriods.last_six_months,
                    label: "Last 6 Months",
                  },
                  { value: TimePeriods.ytd, label: "YTD" },
                  { value: TimePeriods.all, label: "All" },
                ]}
              />
            )}
          </Space>

          {rewards.length <= 1 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ padding: "20px" }}
            />
          ) : (
            <>
              <p className={styles.axisYLabel}>ETH</p>
              <ReactApexChart
                options={state.options as ApexOptions}
                series={state.series as any}
                type="area"
                height={350}
              />
            </>
          )}
        </div>
      </Col>
      <Col xs={{ span: 24 }} lg={{ span: 24 }} className={styles.tableWrapper}>
        <Table
          columns={rewardsColumns}
          dataSource={rewards.reverse()}
          rowKey={"transaction_id"}
          scroll={{ x: true, scrollToFirstRowOnChange: true }}
        />
      </Col>
    </Row>
  );
};

export default RewardsTable;
